/**
 * Musical weighting helpers shared by the spike-family patterns. Both ramp with
 * chart `progress` and spike hard inside a kiai (the song's drops), so the big
 * commitments — tall spikes and wide spike-walls — land on the loudest moments
 * while calm verses stay gentle. Pure functions of their inputs (one RNG roll +
 * the musical context), so they stay deterministic and unit-testable.
 */

/**
 * Spike-cluster sizing (a Geometry-Dash double/triple/quad: adjacent spike
 * columns). The per-hit "weight" rises inside a kiai and ramps with progress;
 * one RNG roll then steps the column count up. Per-tier caps (`maxCols`) keep
 * Normal to doubles and let the fast tiers reach borderline-fair triples/quads.
 */
const CLUSTER_WEIGHT_BASE = 0.12;
const CLUSTER_WEIGHT_KIAI = 0.55;
const CLUSTER_WEIGHT_PROGRESS = 0.28;
const CLUSTER_TRIPLE_FRAC = 0.5;
const CLUSTER_QUAD_FRAC = 0.22;

/**
 * Tall-spike weighting: how often a LONE spike stands TALL (a full-jump
 * obstacle) rather than LOW (a short hop). Higher inside a kiai and ramping with
 * progress, so the big jumps land on the song's louder moments.
 */
const TALL_SPIKE_WEIGHT_BASE = 0.18;
const TALL_SPIKE_WEIGHT_KIAI = 0.6;
const TALL_SPIKE_WEIGHT_PROGRESS = 0.3;

/**
 * Pick how many adjacent columns a tap's spike cluster spans. One RNG `roll` is
 * compared against a per-hit weight (higher in a kiai, ramping with progress) to
 * step up from a lone spike to a double / triple / quad, then the width is
 * shrunk until it fits the available beat room and the chart bounds. Clustering
 * only ever widens a spike that already had room, so it can't regress the
 * density-driven monotonicity or reachability.
 */
export function pickClusterCols(args: {
  roll: number;
  inKiai: boolean;
  progress: number;
  maxCols: number;
  roomBeats: number;
  minGapBeats: number;
  cellBeats: number;
}): number {
  const { roll, inKiai, progress, maxCols, roomBeats, minGapBeats, cellBeats } = args;
  if (maxCols <= 1) {
    return 1;
  }
  const clamped = Math.min(1, Math.max(0, progress));
  const weight = Math.min(
    1,
    (inKiai ? CLUSTER_WEIGHT_KIAI : CLUSTER_WEIGHT_BASE) + CLUSTER_WEIGHT_PROGRESS * clamped,
  );
  let desired = 1;
  if (roll < weight) {
    desired += 1;
  }
  if (roll < weight * CLUSTER_TRIPLE_FRAC) {
    desired += 1;
  }
  if (roll < weight * CLUSTER_QUAD_FRAC) {
    desired += 1;
  }
  desired = Math.min(desired, maxCols);
  let cols = desired;
  while (cols > 1 && roomBeats < minGapBeats + cols * cellBeats) {
    cols -= 1;
  }
  return cols;
}

/**
 * Roll whether a LONE spike stands TALL (a full-jump obstacle, perfect zone
 * ≈ 42 units) rather than LOW (a short hop, ≈ 39 units). Mirrors
 * {@link pickClusterCols}'s weighting so height variety tracks the music.
 */
export function rollTallSpike(roll: number, inKiai: boolean, progress: number): boolean {
  const clamped = Math.min(1, Math.max(0, progress));
  const weight = Math.min(
    1,
    (inKiai ? TALL_SPIKE_WEIGHT_KIAI : TALL_SPIKE_WEIGHT_BASE) + TALL_SPIKE_WEIGHT_PROGRESS * clamped,
  );
  return roll < weight;
}
